import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/navbar/navbar';
import Footer from '../components/footer/footer';
import './Earn.css';
import SEO from '../components/SEO';

const EARN_DATA = {
  rides: {
    title: "City Rides Hailing",
    subtitle: "Drive and keep 100% of your customer fares.",
    desc: "Tired of sharing 25% of your hard-earned fares with platform coordinators? At 9tro Ride, we use a simple flat-rate weekly or monthly subscription model. You match with local passengers and keep every single Naira of the fare.",
    benefits: [
      "Keep 100% of your passenger trip fares",
      "Flexible schedule - choose when and where to drive",
      "Priority customer and road safety dispatcher support",
      "Instant bank withdrawals from your 9tro Driver Wallet"
    ],
    ctaRole: "driver",
    icon: "🚗"
  },
  courier: {
    title: "Courier Dispatch Delivery",
    subtitle: "Deliver packages using your motorbike or bicycle.",
    desc: "Earn flexible income by fulfilling document dispatches, e-commerce orders, and grocery pickups. Our local algorithms route delivery requests to match your vehicle capacity, saving you fuel and battery life.",
    benefits: [
      "Quick delivery routing maps",
      "Deliver packages matching your capacity (Bicycle: 5kg, Motorbike: 20kg)",
      "Insured deliveries for peace of mind",
      "Earn tips directly from customers, kept 100% by you"
    ],
    ctaRole: "courier",
    icon: "🏍️"
  },
  freight: {
    title: "Freight Logistics & Cargo",
    subtitle: "Earn high-revenue orders with your cargo truck or van.",
    desc: "Register your heavy duty truck, box van, or pickup truck to fulfill bulk logistics requests, merchant movements, and warehouse-to-retail deliveries. Get matched with high-value contracts and freight cargo tasks.",
    benefits: [
      "Fulfill heavy cargo contracts (Max 500kg - 10,000kg)",
      "Scheduled bookings for predictable logistics operations",
      "Optimized highway routes for large vehicles",
      "Comprehensive freight insurance coverage"
    ],
    ctaRole: "courier",
    icon: "🚚"
  },
  business: {
    title: "Delivery for Business",
    subtitle: "Empower your corporate logistics and vendor deliveries.",
    desc: "Need to ship retail items, restaurant foods, or office documents? The 9tro Business portal offers custom merchant dashboards and priority dispatcher matching to ensure your customer orders arrive within minutes.",
    benefits: [
      "Access merchant dispatch dashboards",
      "Fulfill multiple orders simultaneously with batch routing",
      "Dedicated delivery routing for corporate offices and shops",
      "Priority support and monthly corporate billing cycles"
    ],
    ctaRole: "business",
    icon: "🏪"
  }
};

function Earn() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Read active tab from URL search parameters, default to 'rides'
  const activeTab = searchParams.get('tab') || 'rides';

  // Sync tab if it's not a valid key
  useEffect(() => {
    if (!Object.keys(EARN_DATA).includes(activeTab)) {
      setSearchParams({ tab: 'rides' });
    }
  }, [activeTab, setSearchParams]);

  const activeData = EARN_DATA[activeTab] || EARN_DATA.rides;

  return (
    <div className="earn-page-wrapper">
      <SEO 
        title={`Earn with 9tro Ride - ${activeData.title}`}
        description="Learn how to drive, deliver packages, manage freight logistics, or leverage corporate business delivery tools with 9tro."
        url="https://www.9troride.com/earn"
      />
      <Navbar showSubNavbar={false} />

      <section className="earn-hero">
        <div className="earn-hero-container">
          <span className="hero-tag">Opportunities</span>
          <h1>Earn on your own terms with 9troride</h1>
          <p>Choose the track that fits your schedule, vehicle capacity, or business size.</p>
        </div>
      </section>

      {/* Tabs Row */}
      <section className="earn-tabs-section">
        <div className="earn-tabs-container">
          <div className="tabs-header-pills">
            {Object.keys(EARN_DATA).map(key => (
              <button 
                key={key} 
                className={`tab-pill-btn ${activeTab === key ? 'active' : ''}`}
                onClick={() => setSearchParams({ tab: key })}
              >
                <span className="tab-pill-icon">{EARN_DATA[key].icon}</span>
                {EARN_DATA[key].title.split(' ')[0]}
              </button>
            ))}
          </div>

          {/* Active Tab Panel */}
          <div className="earn-content-panel">
            <div className="earn-panel-layout">
              <div className="earn-panel-text">
                <span className="panel-icon-badge">{activeData.icon}</span>
                <h2>{activeData.title}</h2>
                <h3>{activeData.subtitle}</h3>
                <p>{activeData.desc}</p>
                
                {activeData.ctaRole === 'business' ? (
                  <button className="earn-apply-btn" onClick={() => navigate('/help')}>Contact Business Desk</button>
                ) : (
                  <button className="earn-apply-btn" onClick={() => navigate(`/apply?role=${activeData.ctaRole}`)}>Apply to Partner</button>
                )}
              </div>

              <div className="earn-panel-benefits">
                <h4>Why partner with 9tro?</h4>
                <ul className="benefits-list">
                  {activeData.benefits.map((benefit, idx) => (
                    <li key={idx}>
                      <span className="bullet-check">✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Earn;
